import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { MailQuestion, Send } from 'lucide-react-native'
import {
  AUTH_PASSWORD_RESET_RESEND_COOLDOWN_SECONDS,
  colors,
  formatCountdown,
} from '@chillout/core'
import { useNavigation, useRoute } from '@react-navigation/native'
import type { NativeStackNavigationProp } from '@react-navigation/native-stack'
import type { RouteProp } from '@react-navigation/native'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import type { AdminAuthStackParamList } from '../../../navigation/types'
import { Button } from '@chillout/ui'
import { InlineAlert } from '@chillout/ui'
import { AdminAuthLayout } from './AdminAuthLayout'
import { AuthScreenHeader } from './AuthScreenHeader'
import { BackToSignInLink } from './BackToSignInLink'
import { getAdminPasswordResetRedirectUrl } from './authUrl'

export function AdminCheckEmailScreen() {
  const { t } = useTranslation()
  const navigation = useNavigation<NativeStackNavigationProp<AdminAuthStackParamList>>()
  const route = useRoute<RouteProp<AdminAuthStackParamList, 'CheckEmail'>>()
  const [resendCooldown, setResendCooldown] = useState(
    AUTH_PASSWORD_RESET_RESEND_COOLDOWN_SECONDS
  )
  const [isResending, setIsResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const canResend = resendCooldown === 0

  useEffect(() => {
    if (resendCooldown === 0) {
      return undefined
    }

    const timer = setTimeout(() => {
      setResendCooldown((currentCooldown) => Math.max(currentCooldown - 1, 0))
    }, 1000)

    return () => clearTimeout(timer)
  }, [resendCooldown])

  async function handleSendAgain() {
    setErrorMessage(null)
    setIsResending(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(route.params.email, {
        redirectTo: getAdminPasswordResetRedirectUrl(),
      })

      if (error) {
        reportUnexpectedError(error)
        setErrorMessage(t('adminAuth.resetLinkRequestError'))
        return
      }

      setResendCooldown(AUTH_PASSWORD_RESET_RESEND_COOLDOWN_SECONDS)
    } catch (err) {
      reportUnexpectedError(err)
      setErrorMessage(t('adminAuth.resetLinkRequestError'))
    } finally {
      setIsResending(false)
    }
  }

  return (
    <AdminAuthLayout>
      <AuthScreenHeader
        Icon={Send}
        iconAccessibilityLabel={t('adminAuth.checkEmailIconAccessibilityLabel')}
        title={t('adminAuth.checkEmailTitle')}
        subtitle={t('adminAuth.checkEmailSubtitle')}
      />
      <View className="mt-8 rounded-lg border border-border bg-canvas-primary sm:mt-10">
        <View className="flex-row gap-4 p-4">
          <View>
            <MailQuestion
              size={32}
              color={colors.accent.DEFAULT}
              strokeWidth={1.8}
              accessibilityLabel={t('adminAuth.checkEmailNoteIconAccessibilityLabel')}
              accessibilityRole="image"
            />
          </View>
          <View className="flex-1">
            <Text className="text-base font-semibold text-primary">
              {t('adminAuth.checkEmailNoteTitle')}
            </Text>
            <Text className="mt-1 text-sm font-normal leading-5 text-secondary">
              {t('adminAuth.checkEmailNoteBody')}
            </Text>
          </View>
        </View>
        <View className="mx-4 h-px bg-border" />
        <Text className="px-4 py-4 text-center text-sm font-normal text-secondary">
          {canResend ? (
            t('adminAuth.resendReadyMessage')
          ) : (
            <>
              {t('adminAuth.resendTimerPrefix')}{' '}
              <Text className="font-semibold text-accent">{formatCountdown(resendCooldown)}</Text>
            </>
          )}
        </Text>
      </View>
      {errorMessage ? (
        <View className="mt-6">
          <InlineAlert message={errorMessage} />
        </View>
      ) : null}
      <Button
        label={t('adminAuth.sendAgainButton')}
        loadingLabel={t('adminAuth.sendingAgainButton')}
        onPress={handleSendAgain}
        variant="secondary"
        disabled={!canResend}
        loading={isResending}
        className="mt-6"
      />
      <BackToSignInLink
        onPress={() => navigation.navigate('SignIn', { email: route.params.email })}
        className="mt-6"
      />
    </AdminAuthLayout>
  )
}
